const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export const DEFAULT_ESIGN_DATE_FORMAT = "MMMM d, yyyy";

export const ESIGN_DATE_FORMAT_PRESETS = [
  "MM/dd/yyyy",
  "dd/MM/yyyy",
  "yyyy-MM-dd",
  "MMMM d, yyyy",
  "d MMM yyyy",
] as const;

export type EsignDateFormatPreset = (typeof ESIGN_DATE_FORMAT_PRESETS)[number];

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Local calendar date from `YYYY-MM-DD` (avoids UTC shift). */
export function parseIsoDateLocal(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(y, mo - 1, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
}

export function toIsoDateString(d: Date): string {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}

function formatWithPreset(d: Date, pattern: string): string {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  switch (pattern) {
    case "MM/dd/yyyy":
      return `${pad2(m)}/${pad2(day)}/${y}`;
    case "dd/MM/yyyy":
      return `${pad2(day)}/${pad2(m)}/${y}`;
    case "yyyy-MM-dd":
      return `${y}-${pad2(m)}-${pad2(day)}`;
    case "MMMM d, yyyy":
      return `${MONTHS_LONG[d.getMonth()]} ${day}, ${y}`;
    case "d MMM yyyy":
      return `${day} ${MONTHS_SHORT[d.getMonth()]} ${y}`;
    default:
      return `${MONTHS_LONG[d.getMonth()]} ${day}, ${y}`;
  }
}

/** Sample date for picker labels (same day, different shapes). */
const FORMAT_SAMPLE_ISO = "2024-07-15";

/** Preset token + example-only label for pickers (value stays the token for storage / PDF). */
export const ESIGN_DATE_FORMAT_OPTIONS: ReadonlyArray<{
  format: EsignDateFormatPreset;
  label: string;
}> = ESIGN_DATE_FORMAT_PRESETS.map((format) => {
  const d = parseIsoDateLocal(FORMAT_SAMPLE_ISO);
  return {
    format,
    label: d ? formatWithPreset(d, format) : format,
  };
});

/**
 * `value` is stored as ISO `YYYY-MM-DD` when possible; legacy free-text dates
 * are passed through or parsed with `Date.parse`.
 */
export function formatStoredDate(
  raw: string,
  dateFormat: string | null | undefined,
): string {
  const t = raw.trim();
  if (!t) return t;
  let d = parseIsoDateLocal(t);
  if (!d) {
    const ms = Date.parse(t);
    if (!Number.isNaN(ms)) d = new Date(ms);
  }
  if (!d || Number.isNaN(d.getTime())) return t;
  const pattern = (dateFormat?.trim() || DEFAULT_ESIGN_DATE_FORMAT) as string;
  return formatWithPreset(d, pattern);
}
